// src/utils/api.js
import axios from "axios";
import Cookies from "js-cookie";

const api = axios.create({
  baseURL: "/api",
  withCredentials: true,
  headers: {
    "X-Requested-With": "XMLHttpRequest",
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// ---------- Cached user (localStorage) ----------
export const getCachedUser = () => {
  const u = localStorage.getItem("cachedUser");
  return u ? JSON.parse(u) : null;
};
export const setCachedUser = (user) => localStorage.setItem("cachedUser", JSON.stringify(user));
export const clearCachedUser = () => localStorage.removeItem("cachedUser");

// ---------- In-memory currentUser ----------
let currentUser = getCachedUser();   // hydrate immediately from localStorage
let userPromise = null;              // single-flight promise tracker

export const setCurrentUser = (u) => {
  currentUser = u;
  if (u) setCachedUser(u);
  else clearCachedUser();
};
export const getCurrentUser = () => currentUser;

// ---------- Improved getUser ----------
export async function getUser(force = false, skipAuthErrorHandling = false) {
  if (!force && currentUser) {
    return currentUser;
  }
  if (!force && userPromise) {
    return userPromise;
  }

  userPromise = api
    .get("/api/user", { meta: { skipAuthErrorHandling } })
    .then((res) => {
      // always save the fresh backend result
      currentUser = res.data;
      setCachedUser(currentUser);
      return currentUser;
    })
    .catch((err) => {
      currentUser = null;
      clearCachedUser();
      throw err;
    })
    .finally(() => {
      userPromise = null;
    });

  return userPromise;
}

// ---------- Session / CSRF ----------
const clearSessionCookies = () => {
  Cookies.remove("XSRF-TOKEN");
  Cookies.remove("laravel_session");
};
export const ensureCsrfToken = async () => api.get("/sanctum/csrf-cookie");

// ---------- Auth helpers ----------
export const forceLogoutIfNeeded = async () => {
  try {
    await ensureCsrfToken();
    await logoutUser();
  } catch {}
};

export const logoutUser = async () => {
  try {
    await ensureCsrfToken();
    await api
      .post("/logout", {}, { meta: { skipAuthErrorHandling: true } })
      .catch((err) => {
        if (err.response?.status !== 401) throw err;
      });
  } finally {
    clearSessionCookies();
    setCurrentUser(null);
  }
};

export const registerUser = async (formData, forceLogout = true) => {
  if (forceLogout) await forceLogoutIfNeeded();
  await ensureCsrfToken();
  await api.post("/register", formData, { meta: { skipAuthErrorHandling: true } });

  // Poll until session is established
  let user = null;
  for (let i = 0; i < 5; i++) {
    try {
      user = await getUser(true, true); // force refetch, skip global 401
      break;
    } catch (err) {
      if (err.response?.status === 401) await new Promise((r) => setTimeout(r, 100));
      else throw err;
    }
  }
  if (!user) throw new Error("Registration failed, session not established.");
  setCurrentUser(user);
  return user;
};

export const loginUser = async (email, password, forceLogout = true) => {
  if (forceLogout) await forceLogoutIfNeeded();
  await ensureCsrfToken();
  const token = Cookies.get("XSRF-TOKEN");
  await api.post(
    "/login",
    { email, password },
    {
      headers: { "X-XSRF-TOKEN": token },
      meta: { skipAuthErrorHandling: true },
    }
  );

  // Poll until session is established
  let user = null;
  for (let i = 0; i < 10; i++) {
    try {
      user = await getUser(true, true); // force refetch
      break;
    } catch {
      await new Promise((r) => setTimeout(r, 200));
    }
  }
  if (!user) throw new Error("Login failed, cookies not applied.");
  setCurrentUser(user);
  return user;
};

// ===============================
// 🔹 Domain APIs (transactions/profile/cards/etc.)
// ===============================
export const getTransactions = (page = 1, filters = {}) =>
  api.get("/api/transactions", { params: { page, ...filters } });

export const exportTransactions = async (filters = {}) => {
  await ensureCsrfToken();
  const res = await api.get("/api/transactions/export", {
    params: filters,
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "transactions.csv");
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const updateProfile = (data) => api.put("/api/user/update", data);
export const updatePassword = (current_password, new_password, new_password_confirmation) =>
  api.put("/api/user/password", {
    current_password,
    new_password,
    new_password_confirmation,
  });
export const uploadAvatar = async (file) => {
  const formData = new FormData();
  formData.append("avatar", file);
  return api.post("/api/user/avatar", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const submitCardApplication = (data) => api.post("/api/card-applications", data);
export const getCardApplications = () => api.get("/api/card-applications");
export const getUserCards = () => api.get("/api/cards");

export const getCurrencies = () => api.get("/api/currencies"); 

export const getCountries = () => api.get("/api/countries");

// ===============================
// 🔹 ADMIN API ENDPOINTS
// ===============================

// Get current admin (auth:sanctum returns user object)
export const getAdminProfile = () => api.get("/api/admin/me");

// Update current admin profile (just proxy to /users/:id/update)
export const updateAdminProfile = (id, data) =>
  api.put(`/api/admin/users/${id}/update`, data);

// Reset/change password for self (reuse resetPassword for admin)
export const changeAdminPassword = (id, payload) =>
  api.post(`/api/admin/users/${id}/reset-password`, payload);


// ===== Dashboard API Endpoints =====
export const getDashboardStats = () => api.get("/api/admin/dashboard-stats");
export const getTransactionTrends = () => api.get("/api/admin/transaction-trends");
export const getBalanceGrowth = () => api.get("/api/admin/balance-growth");
// Users Management API
export const fetchUsers = async (params = {}) => {
  const res = await api.get("/api/admin/users", { params });
  return res.data; // <-- now Users.jsx gets the pure payload
};
export const fetchUserById = (id) => api.get(`/api/admin/users/${id}`);
export const updateUser = (id, data) => api.put(`/api/admin/users/${id}/update`, data);
export const resetPassword = (id) => api.post(`/api/admin/users/${id}/reset-password`);
export const fundUser = (id, payload) => api.post(`/api/admin/users/${id}/fund`, payload);
// Cards Management API
export const fetchCards = async (params = {}) => {
  const res = await api.get("/api/admin/cards", { params });
  return res.data; // Laravel returns pagination JSON
};

export const blockCard = (id) => api.post(`/api/admin/cards/${id}/block`);
export const unblockCard = (id) => api.post(`/api/admin/cards/${id}/unblock`);

export const adjustCardBalance = (id, payload) =>
  api.post(`/api/admin/cards/${id}/adjust-balance`, payload);
// CARD APPLICATIONS
export const fetchCardApplications = async (params = {}) => {
  const res = await api.get("/api/admin/card-applications", { params });
  return res.data;
};

export const approveCardApplication = async (id) => {
  const res = await api.post(`/api/admin/card-applications/${id}/approve`);
  return res.data;
};

export const rejectCardApplication = async (id, note = null) => {
  const res = await api.post(`/api/admin/card-applications/${id}/reject`, { note });
  return res.data;
};
// ===============================
// 🔹 ADMIN TRANSACTIONS API
// ===============================
export const fetchTransactions = async (params = {}) => {
  const res = await api.get("/api/admin/transactions", { params });
  return res.data; // Laravel returns pagination JSON
};

export const approveTransaction = async (id) => {
  const res = await api.post(`/api/admin/transactions/${id}/approve`);
  return res.data;
};

export const rejectTransaction = async (id) => {
  const res = await api.post(`/api/admin/transactions/${id}/reject`);
  return res.data;
};
// ===============================
// 🔹 ADMIN KYC API
// ===============================
export const fetchKycSubmissions = async (params = {}) => {
  const res = await api.get("/api/admin/kyc-submissions", { params });
  return res.data; // Laravel pagination JSON
};

export const approveKyc = async (id) => {
  const res = await api.post(`/api/admin/kyc/${id}/approve`);
  return res.data;
};

export const rejectKyc = async (id, note = null) => {
  const res = await api.post(`/api/admin/kyc/${id}/reject`, { note });
  return res.data;
};

// ---------------- Currencies ---------------- //
export const fetchCurrencies = () => api.get("/api/admin/currencies");
export const createCurrency = (data) => api.post("/api/admin/currencies", data);
export const updateCurrency = (id, data) => api.put(`/api/admin/currencies/${id}`, data);
export const deleteCurrency = (id) => api.delete(`/api/admin/currencies/${id}`);

// ===============================
// 🔹 ADMIN CODES API
// ===============================

// Fetch codes (with optional filters: type, is_active, page, etc.)
export const fetchCodes = async (params = {}) => {
  const res = await api.get("/api/admin/codes", { params });
  return res.data; // Laravel pagination JSON
};

// Create a new code manually
export const createCode = (data) => api.post("/api/admin/codes", data);

// Auto-generate a code (backend will randomize)
export const generateCode = (data) => api.post("/api/admin/codes/generate", data);

// Update an existing code
export const updateCode = (id, data) => api.put(`/api/admin/codes/${id}`, data);

// Toggle active/inactive
export const toggleCode = (id) => api.post(`/api/admin/codes/${id}/toggle`);

// Delete a code
export const deleteCode = (id) => api.delete(`/api/admin/codes/${id}`);

// ===============================
// 🔹 Request Interceptor (uses in-memory currentUser for KYC)
api.interceptors.request.use(
  (config) => {
    const token = Cookies.get("XSRF-TOKEN");
    if (token) config.headers = { ...config.headers, "X-XSRF-TOKEN": token };
    config.withCredentials = true;

    const u = getCurrentUser();
    if (u?.kyc_status && u.kyc_status !== "approved") {
      const blockedRoutes = [
        "/api/transactions",
        "/api/withdraw",
        "/api/cards",
        "/api/deposits",
        "/api/local-transfer",
        "/api/international-transfer",
      ];
      const url = (config.url || "").toString();
      if (blockedRoutes.some((r) => url.includes(r))) {
        return Promise.reject({
          response: { status: 403, data: { message: "KYC not completed" } },
        });
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ---------- Response interceptor ----------
let kycSnackbarShown = false;
let sessionExpiredHandled = false;

window.addEventListener("popstate", () => {
  kycSnackbarShown = false;
});

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const skipAuth = err.config?.meta?.skipAuthErrorHandling;
    const path = window.location?.pathname || "/";
    const onAuthRoute = /^\/(login|register|verify-email|forgot-password)/.test(path);

    // CSRF refresh
    if (err.response?.status === 419 || err.response?.data?.message === "CSRF token mismatch.") {
      try {
        await ensureCsrfToken();
        err.config.headers = {
          ...(err.config.headers || {}),
          "X-XSRF-TOKEN": Cookies.get("XSRF-TOKEN"),
        };
        err.config.withCredentials = true;
        return api.request(err.config);
      } catch (refreshErr) {
        return Promise.reject(refreshErr);
      }
    }

    // KYC 403 (server-side)
    if (err.response?.status === 403 && err.response.data?.message === "KYC not completed") {
      if (!kycSnackbarShown) {
        kycSnackbarShown = true;
        window.dispatchEvent(
          new CustomEvent("showSnackbar", {
            detail: { message: "KYC not completed", severity: "warning" },
          })
        );
        if (window.history.state?.idx > 0) window.history.back();
        else window.location.href = "/user/dashboard";
      }
      return Promise.reject(err);
    }

    // Session expired
    if (!skipAuth && err.response?.status === 401) {
      if (onAuthRoute || !getCachedUser()) {
        return Promise.reject(err);
      }
      if (!sessionExpiredHandled) {
        sessionExpiredHandled = true;
        clearSessionCookies();
        setCurrentUser(null);
        window.dispatchEvent(
          new CustomEvent("showSnackbar", {
            detail: { message: "Session expired. Please log in again.", severity: "error" },
          })
        );
        setTimeout(() => {
          sessionExpiredHandled = false;
          window.location.href = "/login";
        }, 1500);
      }
    }

    return Promise.reject(err);
  }
);

export default api;